import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import ButtonOne from "../../common/normal-button";
import CustomTabs from "../../common/custom-tabs";
import MultiSelectDropdown from "../../common/multi-select-dropdown";
import StatTile from "../../common/stat-tile";
import typography from "../../cssUtils/typography";
import { ColorsConstants, borderColors, dashboardCardGreenTextColor, grayFontColor } from "../../cssUtils/constants";
import {
  MEMBERS_BULK_ENDPOINT,
  MEMBERS_ENDPOINT,
  MEMBERS_EXPORT_ENDPOINT,
  MEMBERS_FILTER_OPTIONS_ENDPOINT,
  MEMBERS_IMPORT_TEMPLATE_ENDPOINT,
  MEMBERS_SUMMARY_ENDPOINT,
} from "../../config/urls";
import { buildQuery, messageFrom } from "./membersApi";
import ExportMenu from "./ExportMenu";
import {
  DEFAULT_FILTERS,
  PAGE_SIZES,
  SORT_KEYS,
  TABS,
  filtersFromSearch,
  filtersToParams,
  filtersToSearch,
  filtersToSummaryParams,
  formatDate,
  formatDateTime,
  hasActiveFilters,
  issueLabel,
  memberStatus,
} from "./membersUtils";

const MAX_EXPORT_IDS = 1000; // the API accepts at most this many ids

const EMPTY_OPTIONS = { classes: [], cities: [], states: [], countries: [] };

const valueItems = (allLabel, values) => [
  { label: allLabel, value: "" },
  ...values.map((value) => ({ label: value, value })),
];

const MembersListView = ({ api, refreshKey, notify, onOpenMember, onAdd, onUpload, onChanged }) => {
  // Filters, tab, search, order and page live in the URL query string, so a reload or a
  // return from a member keeps the view.
  const [filters, setFilters] = useState(() => filtersFromSearch(window.location.search));
  const [searchText, setSearchText] = useState(filters.q);
  const [data, setData] = useState({ count: 0, results: [] });
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const reload = useCallback(() => {
    setReloadTick((tick) => tick + 1);
    onChanged?.();
  }, [onChanged]);

  const changeFilters = useCallback((patch, { keepSelection = false } = {}) => {
    setFilters((prev) => ({ ...prev, page: 0, ...patch }));
    if (!keepSelection) setSelected({});
  }, []);

  // Search: debounced 300 ms, at least 2 characters.
  useEffect(() => {
    const trimmed = searchText.trim();
    const effective = trimmed.length >= 2 ? trimmed : "";
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.q === effective ? prev : { ...prev, q: effective, page: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const listParams = useMemo(() => filtersToParams(filters), [filters]);
  const summaryParams = useMemo(() => filtersToSummaryParams(filters), [filters]);

  useEffect(() => {
    const keepInUrl = () =>
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${filtersToSearch(filters)}`);
    keepInUrl();
    // The sidebar rewrites the URL (without the query) once on first load, after this runs.
    const timer = setTimeout(keepInUrl, 0);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    api
      .request(`${MEMBERS_ENDPOINT}${buildQuery(listParams)}`, { signal: controller.signal })
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setLoadError(messageFrom(error, "We couldn't load the members. Please try again."));
        setLoading(false);
      });
    return () => controller.abort();
  }, [api, listParams, refreshKey, reloadTick]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .request(`${MEMBERS_SUMMARY_ENDPOINT}${buildQuery(summaryParams)}`, { signal: controller.signal })
      .then(setSummary)
      .catch(() => {});
    return () => controller.abort();
  }, [api, summaryParams, refreshKey, reloadTick]);

  useEffect(() => {
    let active = true;
    api
      .request(MEMBERS_FILTER_OPTIONS_ENDPOINT)
      .then((result) => active && setOptions({ ...EMPTY_OPTIONS, ...result }))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [api, refreshKey, reloadTick]);

  // ---- selection ---------------------------------------------------------
  const rows = data.results;
  const selectedIds = Object.keys(selected).map(Number);
  const hasSelectedAll = rows.length > 0 && rows.every((row) => selected[row.id]);

  const toggleAll = () =>
    setSelected((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (hasSelectedAll) delete next[row.id];
        else next[row.id] = true;
      });
      return next;
    });

  const toggleOne = (event, row) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id];
      else next[row.id] = true;
      return next;
    });

  // ---- actions -----------------------------------------------------------
  const runBulk = async (action, successText) => {
    setBusy(true);
    try {
      const result = await api.request(MEMBERS_BULK_ENDPOINT, { method: "POST", body: { action, ids: selectedIds } });
      notify(successText(result.updated));
      setSelected({});
      reload();
    } catch (error) {
      notify(messageFrom(error), { severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  const plural = (n) => `${n} ${n === 1 ? "member" : "members"}`;

  // Export CSV for the ticked rows (the header's Export menu covers the other scopes and Excel).
  const exportSelectedCsv = async () => {
    if (selectedIds.length > MAX_EXPORT_IDS) {
      notify(`Select at most ${MAX_EXPORT_IDS} members to export.`, { severity: "error" });
      return;
    }
    setBusy(true);
    try {
      const params = { format: "csv", scope: "selected", ids: selectedIds.join(","), ordering: filters.ordering };
      await api.download(`${MEMBERS_EXPORT_ENDPOINT}${buildQuery(params)}`, "members.csv");
      notify(`Exported ${plural(selectedIds.length)} as CSV`);
    } catch (error) {
      notify(messageFrom(error, "The export failed. Please try again."), { severity: "error" });
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async (format) => {
    try {
      await api.download(`${MEMBERS_IMPORT_TEMPLATE_ENDPOINT}?format=${format}`, `members_template.${format}`);
    } catch (error) {
      notify(messageFrom(error), { severity: "error" });
    }
  };

  // ---- table -------------------------------------------------------------
  const sortKey = filters.ordering.replace(/^-/, "");
  const sortColumn = Object.keys(SORT_KEYS).find((id) => SORT_KEYS[id] === sortKey);
  const sortState = sortColumn ? [{ id: sortColumn, direction: filters.ordering.startsWith("-") ? "desc" : "asc" }] : [];

  const changeSort = (columnId) => {
    const key = SORT_KEYS[columnId];
    let ordering = key; // none -> ascending -> descending -> none
    if (filters.ordering === key) ordering = `-${key}`;
    else if (filters.ordering === `-${key}`) ordering = "";
    changeFilters({ ordering }, { keepSelection: true });
  };

  const columns = [
    {
      id: "member",
      label: "Member",
      sortable: true,
      minWidth: 240,
      render: (row) => (
        <Box>
          <Typography
            component="button"
            type="button"
            onClick={() => onOpenMember(row.id)}
            sx={{
              ...typography.bodyLarge,
              fontWeight: 600,
              color: ColorsConstants.secondaryBackgroundColor,
              background: "none",
              border: 0,
              p: 0,
              cursor: "pointer",
              textAlign: "left",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {row.name || "No name"}
          </Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{row.membership_id || "No Membership ID"}</Typography>
        </Box>
      ),
    },
    { id: "class", label: "Class", render: (row) => row.membership_class_name || "—" },
    { id: "mobile", label: "Mobile", render: (row) => row.mobile },
    { id: "city", label: "City", render: (row) => row.city || "—" },
    { id: "since", label: "Member since", sortable: true, render: (row) => formatDate(row.member_since) },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (row) => {
        const status = memberStatus(row);
        return <AppChip label={status.label} color={status.color} />;
      },
    },
    {
      id: "data",
      label: "Data",
      render: (row) => (
        <AppChip label={issueLabel(row.data_issue_count)} color={row.data_issue_count ? "warning" : "success"} />
      ),
    },
  ];

  const filtersActive = hasActiveFilters(filters);
  const showEmpty = !loading && !loadError && data.count === 0;
  const tabs = TABS.map((tab) => ({
    ...tab,
    label: `${tab.label}${summary ? ` (${summary.tabs[tab.value] ?? 0})` : ""}`,
  }));
  const lastUpload = summary?.last_upload;
  const total = summary?.cards.total;

  const clearFilters = () => {
    setSearchText("");
    changeFilters({ ...DEFAULT_FILTERS, ordering: filters.ordering, pageSize: filters.pageSize });
  };

  const dropdownLabel = (name, value) => (value ? `${name}: ${value}` : name);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography component="h1" sx={{ ...typography.h2, color: ColorsConstants.primaryBlue100 }}>
            Members
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
            {total === undefined ? "Loading…" : `${plural(total)}`}
            {lastUpload ? ` · last upload ${lastUpload.file_name} · ${formatDate(lastUpload.at)}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <ButtonOne onClick={onAdd}>Add member</ButtonOne>
          <ButtonOne variant="outlined" startIcon={<UploadFileIcon />} onClick={onUpload}>
            Upload file
          </ButtonOne>
          <ExportMenu
            api={api}
            notify={notify}
            filters={filters}
            filteredCount={data.count}
            totalCount={total}
            selectedIds={selectedIds}
          />
        </Box>
      </Box>

      {/* Summary */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }} aria-label="Summary">
        <StatTile labelFirst value={summary?.cards.total ?? "—"} label="Total members" />
        <StatTile labelFirst value={summary?.cards.annual ?? "—"} label="Annual members" />
        <StatTile labelFirst value={summary?.cards.life ?? "—"} label="Life members" valueColor={ColorsConstants.primaryBlue90} />
        <StatTile
          labelFirst
          value={summary?.cards.reachable ?? "—"}
          label="Reachable on WhatsApp"
          valueColor={dashboardCardGreenTextColor}
        />
        <StatTile labelFirst value={summary?.cards.needs_attention ?? "—"} label="Needs attention" valueColor={ColorsConstants.error} />
      </Box>

      {/* Tabs */}
      <CustomTabs value={filters.tab} tabs={tabs} onChange={(event, value) => changeFilters({ tab: value })} />

      {/* Search (full width) with the Class filter on the same row */}
      <Box
        role="search"
        aria-label="Search members and filter by class"
        sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}
      >
        {/* The two share the row 0.8 : 0.2. Each cell has a minimum width, so on a narrow screen
            the Class filter wraps below the search instead of being squeezed. */}
        <Box data-testid="members-search-cell" sx={{ flex: "0.8 1 0", minWidth: 240 }}>
          <TextField
            fullWidth
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search name, membership ID, mobile, email, city"
            inputProps={{ "aria-label": "Search members" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box data-testid="members-class-cell" sx={{ flex: "0.2 1 0", minWidth: 180 }}>
          <MultiSelectDropdown
            placeholder="Class"
            searchPlaceholder="Search classes"
            options={options.classes.map((item) => ({ label: item.name, value: String(item.id) }))}
            value={filters.classIds}
            onChange={(classIds) => changeFilters({ classIds })}
            sx={{ height: 40 }} // the dropdown defaults to 56px; match the search box
          />
        </Box>
      </Box>

      {/* Other filters */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <AppDropdown
          searchable
          menuItems={valueItems("All cities", options.cities)}
          selectedValue={filters.city}
          onMenuSelect={(city) => changeFilters({ city })}
        >
          {dropdownLabel("City", filters.city)}
        </AppDropdown>
        <AppDropdown
          searchable
          menuItems={valueItems("All states", options.states)}
          selectedValue={filters.state}
          onMenuSelect={(state) => changeFilters({ state })}
        >
          {dropdownLabel("State", filters.state)}
        </AppDropdown>
        <AppDropdown
          searchable
          menuItems={valueItems("All countries", options.countries)}
          selectedValue={filters.country}
          onMenuSelect={(country) => changeFilters({ country })}
        >
          {dropdownLabel("Country", filters.country)}
        </AppDropdown>
        {filtersActive && (
          <Button onClick={clearFilters} sx={{ ...typography.bodyLarge, textTransform: "none" }}>
            Clear filters
          </Button>
        )}
      </Box>

      {/* Bulk bar */}
      {selectedIds.length > 0 && (
        <Box
          role="toolbar"
          aria-label="Bulk actions"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            px: 2,
            py: 1,
            borderRadius: "10px",
            backgroundColor: ColorsConstants.primaryBlue60,
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          }}
        >
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{selectedIds.length} selected</Typography>
          <ButtonOne variant="outlined" disabled={busy} onClick={exportSelectedCsv}>
            Export CSV
          </ButtonOne>
          <ButtonOne
            variant="outlined"
            disabled={busy}
            onClick={() => runBulk("deactivate", (n) => `${plural(n)} marked inactive`)}
          >
            Mark inactive
          </ButtonOne>
          <Button onClick={() => setSelected({})} sx={{ textTransform: "none" }}>
            Clear
          </Button>
        </Box>
      )}

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {/* Table or empty state */}
      {showEmpty ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            border: `1px dashed ${ColorsConstants.inputFieldGrey}`,
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography sx={{ ...typography.h2 }}>{filtersActive ? "No members match" : "No members yet"}</Typography>
          <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
            {filtersActive
              ? "Try a different search or clear the filters."
              : "Upload your member file to get started."}
          </Typography>
          {filtersActive ? (
            <ButtonOne onClick={clearFilters}>Clear filters</ButtonOne>
          ) : (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <ButtonOne startIcon={<UploadFileIcon />} onClick={onUpload}>
                Upload file
              </ButtonOne>
              <ButtonOne variant="outlined" onClick={() => downloadTemplate("xlsx")}>
                Download template
              </ButtonOne>
            </Box>
          )}
        </Box>
      ) : (
        <AppTable
          columns={columns}
          rows={rows}
          rowKey="id"
          loading={loading}
          selectable
          hasSelectedAll={hasSelectedAll}
          onSelectAll={toggleAll}
          isRowSelected={(row) => Boolean(selected[row.id])}
          onSelectRow={toggleOne}
          count={data.count}
          page={filters.page}
          rowsPerPage={filters.pageSize}
          rowsPerPageOptions={PAGE_SIZES}
          onPageChange={(event, page) => changeFilters({ page }, { keepSelection: true })}
          onRowsPerPageChange={(event) =>
            changeFilters({ pageSize: Number(event.target.value) }, { keepSelection: true })
          }
          sortState={sortState}
          onSortChange={changeSort}
        />
      )}
      {rows.length > 0 && (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
          Showing {rows.length} of {data.count} {data.count === 1 ? "member" : "members"}
          {summary?.last_upload ? ` · last upload ${formatDateTime(summary.last_upload.at)}` : ""}
        </Typography>
      )}
    </Box>
  );
};

export default MembersListView;
